import { FC, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import { useSnackbar, VariantType } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { logActivity } from "@/api";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

import { deleteInquiry } from "../inquiries.api";
import type { Inquiry } from "../inquiries.types";

interface DeleteInquiryModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: (defaultInfo?: Inquiry) => void;
  onCancel?: () => void;
  defaultInfo: Inquiry;
  buttonProps?: {
    cancelText?: string;
    submitText?: string;
  };
}

export const DeleteInquiryModal: FC<DeleteInquiryModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  defaultInfo,
  buttonProps,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await deleteInquiry(defaultInfo.inquiry_id);

      enqueueSnackbar("Inquiry Deleted.", {
        variant: "success" as VariantType,
      });

      logActivity(
        "Admin",
        currentUser.user_id,
        `Deleted Inquiry ${defaultInfo.inquiry_id}`
      );

      queryClient.invalidateQueries({ queryKey: ["inquiries:list"] });
      queryClient.invalidateQueries({ queryKey: ["inquiries"] });

      onSubmit?.(defaultInfo);
      onClose();
    } catch (error) {
      console.error("Error deleting inquiry:", error);
      enqueueSnackbar("Error deleting inquiry.", {
        variant: "error" as VariantType,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    onCancel?.();
    onClose();
  };

  const label =
    defaultInfo?.request_name || `Inquiry ID ${defaultInfo?.inquiry_id}`;

  return (
    <Modal open={show} onClose={handleCancel}>
      <Modal.Title>Delete Inquiry</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to delete the inquiry:{" "}
        <span style={{ fontWeight: 800 }}>"{label}"</span>?
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete Inquiry"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default DeleteInquiryModal;
